import { paramsToQuery } from '@/base/MaUtils'
import { cacheRequest, makeRequest } from '@/base/MaRequestPooler'

export function getAppAnalysis({ trackId, startDate, endDate }, optionalParams) {
  return cacheRequest(`/market-estimation/app/${trackId}?startDate=${startDate}&endDate=${endDate}&${paramsToQuery(optionalParams)}`)
}

export function getPublisherAnalysis({ storeName, publisherId, startDate, endDate }, optionalParams) {
  return cacheRequest(`/market-estimation/store/${storeName}/publisher/${publisherId}?startDate=${startDate}&endDate=${endDate}&${paramsToQuery(optionalParams)}`)
}

export function getStoreAnalysis({ storeName, startDate, endDate }, optionalParams) {
  return cacheRequest(`/market-estimation/store/${storeName}?startDate=${startDate}&endDate=${endDate}&${paramsToQuery(optionalParams)}`)
}

export function getTopApps({ storeName, year, month }, optionalParams) {
  return cacheRequest(`/market-estimation/store/${storeName}/top-apps/${year}/${month}/v2?${paramsToQuery(optionalParams)}`)
}

export function getTopPublishers({ storeName, year, month }, optionalParams) {
  return cacheRequest(`/market-estimation/store/${storeName}/top-publishers/${year}/${month}?${paramsToQuery(optionalParams)}`)
}

export function getBulkEstimation({ storeName, trackIds, startDate, endDate, region, device }) {
  return makeRequest(`/market-estimation/v2/store/${storeName}/apps?startDate=${startDate}&endDate=${endDate}&countries=${region}&device=${device}`, { method: 'POST', data: trackIds })
}

/**
 * Returns total download/revenue estimations for the given trackId.
 * @param trackId
 * @returns {Promise<any>}
 */
export function getTotalEstimation({ trackId }) {
  return cacheRequest(`/market-estimation/total/app/${trackId}`)
}

/**
 * Fetches the download and revenue data for a set of trackIds within specified parameters.
 *
 * @function
 * @name getDownloadAndRevenue
 * @param {string[]} trackIds - An array of track IDs to retrieve data for.
 * @param {object} options - Configuration options for the request.
 * @param {string} options.store - The store to get data from (e.g., "ios", "play").
 * @param {string} options.startDate - The start date for data retrieval in YYYY-MM-DD format.
 * @param {string} options.endDate - The end date for data retrieval in YYYY-MM-DD format.
 * @param {string[]} [options.countries] - An optional array of country codes to filter by.
 * @param {string} [options.device] - An optional device type to filter by (e.g., "iphone", "android").
 * @returns {Promise<object>} Returns a promise that resolves with the download and revenue data.
 */
export function getDownloadAndRevenue(trackIds, { store, startDate, endDate, countries, device }) {
  const params = { startDate, endDate, countries, device }
  return makeRequest(`/market-estimation/${store}/app/estimations/daily?${paramsToQuery(params)}`, {
    method: 'POST',
    data: trackIds,
  })
}

/**
 * Returns searched apps info.
 * @param {string} store
 * @param {Array} trackIds
 * @param {Array} countries
 * @param {string} month
 * @param {string} year
 * @param {number} category
 * @param {string} device
 * @param {number} limit table current page size
 * @returns {Promise<any>}
 */
export function searchInTopApps({
  store,
  trackIds,
  countries,
  month,
  year,
  category,
  device,
  limit,
}) {
  const query = paramsToQuery({
    trackIds,
    countries,
    month,
    year,
    category,
    device,
    limit,
  })
  return makeRequest(`/market-estimation/store/${store}/top-apps/search?${query}`)
}

/**
 * Returns searched publishers info.
 * @param {string} store
 * @param {Array} publisherIds
 * @param {Array} countries
 * @param {string} month
 * @param {string} year
 * @param {number} category
 * @param {string} device
 * @param {number} limit table current page size
 * @returns {Promise<any>}
 */
export function searchInTopPublishers({
  store,
  publisherIds,
  countries,
  month,
  year,
  category,
  device,
  limit,
}) {
  const query = paramsToQuery({
    publisherIds,
    countries,
    month,
    year,
    category,
    device,
    limit,
  })
  return makeRequest(`/market-estimation/store/${store}/top-publishers/search?${query}`)
}

/**
 * Fetches the publisher names from a list of developer IDs.
 * @param {string} storeName - The store name (e.g., "ios", "play").
 * @param {string[]} developerList - An array of developer IDs.
 * @returns {Promise<any>}
 */
export function getPublisherNames(storeName, developerList) {
  return makeRequest(`/app/${storeName}/publisher/multiple`, {
    method: 'POST',
    data: developerList,
  })
}
